import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, Pin, Search, Plus } from 'lucide-react';
import { useAnnouncements, type AnnouncementListItem } from '@/lib/queries';
import { useAuthStore } from '@/store/auth.store';
import { initials } from '@isoft-people/utils';

const AUDIENCE_LABELS: Record<string, string> = {
  ALL: 'Todos',
  BY_COUNTRY: 'Por país',
  BY_DEPARTMENT: 'Por departamento',
  BY_COUNTRY_AND_DEPARTMENT: 'País y depto.',
};

function timeAgo(dateStr: string) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `Hace ${days}d`;
  return d.toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function AnnouncementsPage() {
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'ADMIN' || role === 'HR';

  const [statusFilter, setStatusFilter] = useState('PUBLISHED');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useAnnouncements({
    status: isAdmin ? statusFilter : undefined,
    search: search || undefined,
    page,
    pageSize: 20,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 20);
  const pinned = items.filter((a) => a.pinned);
  const regular = items.filter((a) => !a.pinned);

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-font-primary">Anuncios</h1>
          <p className="mt-1 text-sm text-font-secondary">
            Comunicados y avisos internos de ISOFT
          </p>
        </div>
        {isAdmin && (
          <Link
            to="/anuncios/nuevo"
            className="flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-primary-dark"
          >
            <Plus size={14} /> Nuevo anuncio
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {isAdmin && (
          <div className="flex rounded-md border border-border bg-white">
            {(['PUBLISHED', 'DRAFT', 'ARCHIVED'] as const).map((s) => (
              <button
                key={s}
                onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`px-4 py-2 text-xs font-semibold transition ${
                  statusFilter === s
                    ? 'bg-primary text-white'
                    : 'text-font-secondary hover:bg-bg'
                } ${s === 'PUBLISHED' ? 'rounded-l-md' : ''} ${s === 'ARCHIVED' ? 'rounded-r-md' : ''}`}
              >
                {s === 'PUBLISHED' ? 'Publicado' : s === 'DRAFT' ? 'Borrador' : 'Archivado'}
              </button>
            ))}
          </div>
        )}
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-font-tertiary" />
          <input
            type="text"
            placeholder="Buscar anuncio..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="h-10 w-full max-w-sm rounded-md border border-border bg-white pl-9 pr-4 text-sm text-font-primary placeholder:text-font-tertiary focus:border-primary focus:outline-none"
          />
        </div>
      </div>

      {isLoading && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg border border-border bg-white" />
          ))}
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="flex flex-col items-center gap-3 py-16">
          <Megaphone size={40} className="text-font-tertiary" />
          <p className="text-sm text-font-tertiary">Sin anuncios por el momento.</p>
        </div>
      )}

      {/* Pinned */}
      {pinned.length > 0 && (
        <div className="rounded-lg bg-primary-subtle p-5">
          <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary">
            <Pin size={12} /> Fijados
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {pinned.map((a) => (
              <AnnouncementCard key={a.id} announcement={a} compact />
            ))}
          </div>
        </div>
      )}

      {/* Regular list */}
      <div className="space-y-3">
        {regular.map((a) => (
          <AnnouncementCard key={a.id} announcement={a} />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-font-secondary">
          <span>
            Mostrando {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} de {total}
          </span>
          <div className="flex gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`flex h-8 w-8 items-center justify-center rounded-md text-xs font-semibold ${
                  p === page
                    ? 'bg-primary text-white'
                    : 'border border-border bg-white text-font-secondary hover:bg-bg'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AnnouncementCard({
  announcement: a,
  compact,
}: {
  announcement: AnnouncementListItem;
  compact?: boolean;
}) {
  const authorName = a.createdBy?.employee
    ? `${a.createdBy.employee.firstName} ${a.createdBy.employee.lastName}`
    : a.createdBy?.email ?? '–';
  const authorPhoto = a.createdBy?.employee?.photoUrl;

  return (
    <Link
      to={`/anuncios/${a.id}`}
      className={`block rounded-lg border bg-white transition hover:border-primary hover:shadow-sm ${
        compact ? 'border-border p-4' : 'border-border p-5'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Unread dot */}
        {!a.isRead && (
          <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
        )}
        {a.isRead && <span className="mt-2 h-2 w-2 flex-shrink-0" />}

        <div className="flex-1 min-w-0">
          <h3
            className={`font-semibold text-font-primary ${
              compact ? 'text-sm' : 'text-base'
            } truncate`}
          >
            {a.pinned && <Pin size={12} className="mr-1 inline text-primary" />}
            {a.title}
          </h3>
          <p className="mt-1 line-clamp-2 text-sm text-font-secondary">{a.body}</p>

          <div className="mt-3 flex items-center gap-3">
            {authorPhoto ? (
              <img
                src={authorPhoto}
                alt={authorName}
                className="h-6 w-6 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-white">
                {initials(authorName)}
              </div>
            )}
            <span className="text-xs text-font-secondary">{authorName}</span>
            <span className="text-xs text-font-tertiary">·</span>
            <span className="text-xs text-font-tertiary">
              {timeAgo(a.publishedAt ?? a.createdAt)}
            </span>
            <span
              className="ml-auto rounded-full bg-primary-subtle px-2 py-0.5 text-[10px] font-semibold text-primary"
            >
              {AUDIENCE_LABELS[a.audienceType] ?? a.audienceType}
            </span>
            {a.status === 'DRAFT' && (
              <span className="rounded-full bg-yellow-50 px-2 py-0.5 text-[10px] font-semibold text-warning">
                Borrador
              </span>
            )}
            {a.status === 'ARCHIVED' && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-font-secondary">
                Archivado
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
